import { UrlObject } from 'url';

import React from 'react';

import { flatMap } from 'lodash-es';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { IconType } from 'react-icons';
import { BiMessageAlt } from 'react-icons/bi';
import { FaUserAstronaut } from 'react-icons/fa';
import {
  HiUserCircle,
  HiCollection,
  HiShieldCheck,
  HiPencil,
  HiLogout,
} from 'react-icons/hi';
import { RiDashboard2Fill } from 'react-icons/ri';

import { transferImageResize } from '@/lib/utils';
import useLoginHook from '@/modules/auth/hooks/useLoginHook';
import useProfile from '@/modules/core/hooks/useProfileHook';
import { IUserData } from '@/modules/core/types';
import { TkTokenHistory } from '@/tayara-kit/icons';

import { UserType } from '../types';

type UserInfo = {
  userData: IUserData;
  onClickMenu?: () => {};
};

interface ISubNav {
  name: string;
  label: string;
  href: UrlObject;
  icon: IconType;
  action?: () => void;
}
const ProfileSidePanel: React.FunctionComponent<UserInfo> = () => {
  const { userData, selectImage } = useProfile();
  const { query } = useRouter();
  const { logout } = useLoginHook();

  const subNavigation: ISubNav[] = flatMap([
    userData.userType === UserType.PRO
      ? {
          name: 'Tableau de bord',
          label: 'dashboard',
          href: { query: { t: 'dashboard' } },
          icon: RiDashboard2Fill,
        }
      : [],
    {
      name: 'Mes annonces',
      label: 'myListings',
      href: { query: { t: 'myListings' } },
      icon: HiCollection,
    },
    {
      name: 'Mes Messages',
      label: 'messages',
      href: { query: { t: 'messages' } },
      icon: BiMessageAlt,
    },
    {
      name: 'Mes transactions',
      label: 'transactions',
      href: { query: { t: 'transactions' } },
      icon: TkTokenHistory,
    },
    {
      name: 'Mes informations',
      label: 'details',
      href: { query: { t: 'details' } },
      icon: HiUserCircle,
    },
    {
      name: 'Sécurité',
      label: 'security',
      href: { query: { t: 'security' } },
      icon: HiShieldCheck,
    },

    {
      name: 'Se déconnecter',
      label: 'logout',
      href: { href: '/' },
      icon: HiLogout,
      action: logout,
    },
  ]);

  // useEffectOnce(() => {
  //   if (!subNavigation.find((item) => item.label === query.t)) {
  //     push({ query: { t: 'myListings' } });
  //   }
  // });

  return (
    <div className="hidden lg:block space-y-1 w-full h-full border-r border-r-neutral-100">
      <div className="flex flex-col items-center gap-3 my-6">
        <div className="relative w-32 h-32 mask mask-squircle overflow-hidden mx-auto">
          {userData?.avatar ? (
            <img
              src={transferImageResize({
                src: userData?.avatar,
              })}
              className="relative w-full h-full"
              alt={`Avatar of ${userData?.username} on tayara`}
            />
          ) : (
            <div className="bg-primary/10 text-primary p-2">
              <FaUserAstronaut className="" size={'100%'} />
            </div>
          )}

          <label
            htmlFor="desktop-user-photo"
            className="transform-gpu absolute inset-0 w-full h-full backdrop-filter backdrop-brightness-75 backdrop-blur-lg transition-opacity bg-opacity-75 flex items-center justify-center text-sm font-medium text-white opacity-0 hover:opacity-100 focus-within:opacity-100"
            style={{
              backdropFilter: 'blur(4px)',
            }}
          >
            <HiPencil className="mr-2" />
            <span>Modifier</span>
            <span className="sr-only">User photo</span>
            <input
              type="file"
              onChange={selectImage}
              id="desktop-user-photo"
              name="user-photo"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-neutral-300 rounded-md"
            />
          </label>
        </div>
        <span className="font-bold -ml-3 text-neutral-700 text-xs capitalize flex gap-1 items-center">
          <Link
            href={{ query: { t: 'details' } }}
            passHref
            className="p-1 rounded-md bg-blue-100"
          >
            <HiPencil className="text-blue-500" />
          </Link>
          <span>{userData?.fullname || 'Annonyme'} </span>
        </span>
      </div>
      <nav className="">
        {subNavigation.map((item) => (
          <Link
            key={item.name}
            href={item.href}
            passHref
            className={` font-extrabold 
          ${
            item.label === query.t
              ? 'bg-primary text-white !px-10 '
              : 'border-transparent text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900'
          }
          group px-8 py-1 flex items-center text-sm`}
            aria-current={item.label === query.t ? 'page' : undefined}
            {...(item.action ? { onClick: logout } : {})}
          >
            <item.icon
              className={`${
                item.label === query.t
                  ? 'text-white group-hover:text-white '
                  : ' group-hover:text-neutral-600 '
              }
            flex-shrink-0 -ml-1 mr-3 h-10 w-6`}
              aria-hidden="true"
            />
            <span className="truncate">{item.name}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default ProfileSidePanel;
